import { useState } from "react";

import { useHistory } from "react-router-dom";

import Box from "@/components/Box";
import Circle from "@/components/Circle";
import {
  CMP_PRACTITIONER_URL,
  CMP_STUDENT_APPLICATION_URL,
  EXPERTS_URL,
  LOGGED_OUT_URL,
  ORDERS_URL,
  SETTINGS_URL,
  TEST_RESULTS_URL_BASE
} from "@/core/urls";
import useLogOut from "@/hooks/header/useLogOut";
import UserIcon from "@/images/user.svg?react";
import ChevronComponent from "@/tpo/Chevron";
import Group from "@/tpo/Group";
import Menu from "@/tpo/Menu";
import BurgerButton from "@/tpo/header/BurgerButton";
import { CloseButton } from "@/v2/Buttons";

import styles from "../common/BurgerMenu.module.css";

export default function BurgerMenu({ accounts = [], burgerButtonProps, bg: _bg = "dark" }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const history = useHistory();
  const bg = !menuOpen ? _bg : "#282828";

  const handleLinkClick = (to, newTab = false) => {
    if (newTab) {
      window.open(to, "_blank");
    } else {
      history.push(to);
    }
    setMenuOpen(false);
  };

  const MenuItem = ({ children, onClick, color = "white", active, ...props }) => (
    <Box
      color={color}
      fontFamily="gilroyMedium"
      fontSize={24}
      lineHeight="150%"
      py={20}
      className={styles.burgerMenuItem}
      cursor="pointer"
      onClick={onClick}
      {...props}
    >
      {active && <Circle bg="green" size={5} position="relative" left="-15px" top="10px" />}
      {children}
    </Box>
  );

  const ExpandableMenuItem = ({
    label,
    isOpen,
    onToggle,
    children,
    collapsableContainerProps,
    leftIcon
  }) => (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        color="white"
        fontFamily="gilroyMedium"
        fontSize={24}
        lineHeight="150%"
        py={20}
        cursor="pointer"
        onClick={onToggle}
        className={styles.burgerMenuExpandableItem}
      >
        <Group color="inherit" alignItems="center" gap={10}>
          {leftIcon}
          {label}
        </Group>
        <ChevronComponent direction={isOpen ? "top" : "bottom"} width={5} fill="white" />
      </Box>
      {isOpen && (
        <Box py={20} {...collapsableContainerProps}>
          {children}
        </Box>
      )}
    </Box>
  );

  const SubMenuItem = ({ label, onClick, active }) => (
    <MenuItem
      onClick={onClick}
      py={20}
      fontSize={16}
      border={0}
      className={styles.subMenuItem}
      active={active}
    >
      {label}
    </MenuItem>
  );

  const logout = useLogOut();

  const links = accounts.flatMap(section => section.links);
  const filteredAccounts = accounts.filter(section => section.links.length > 0);

  const mobileBurgerMenuContent = (
    <>
      {links.length > 1 && (
        <ExpandableMenuItem
          label="Accounts"
          isOpen={accountsOpen}
          onToggle={() => setAccountsOpen(!accountsOpen)}
          leftIcon={<UserIcon fill="white" width={20} />}
        >
          <Box pl={0}>
            {filteredAccounts.map(section => (
              <Box key={section.label}>
                <Box
                  color="midGrey"
                  fontFamily="gilroyMedium"
                  fontSize={14}
                  lineHeight="150%"
                  pb={16}
                >
                  {section.label}
                </Box>
                <Box pl={28}>
                  {section.links.map(link => (
                    <SubMenuItem
                      key={link.label}
                      label={link.label}
                      onClick={() => handleLinkClick(link.href || link.to, link.newTab)}
                      active={link.activeTest?.()}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </ExpandableMenuItem>
      )}
      <MenuItem onClick={() => handleLinkClick(EXPERTS_URL)}>Find a practitioner</MenuItem>
      <MenuItem onClick={() => handleLinkClick(TEST_RESULTS_URL_BASE)}>Test Results</MenuItem>
      <MenuItem onClick={() => handleLinkClick(ORDERS_URL)}>Orders</MenuItem>
      <MenuItem onClick={() => handleLinkClick(SETTINGS_URL)}>Profile</MenuItem>
      <MenuItem onClick={() => handleLinkClick(CMP_PRACTITIONER_URL)}>
        Practitioner application
      </MenuItem>
      <MenuItem onClick={() => handleLinkClick(CMP_STUDENT_APPLICATION_URL)}>
        Student application
      </MenuItem>
      <MenuItem onClick={logout} color="red" borderBottom="none">
        Log out
      </MenuItem>
    </>
  );

  const burgerMenuContent = (
    <>
      <MenuItem onClick={() => handleLinkClick(EXPERTS_URL)}>Find a practitioner</MenuItem>
      <MenuItem onClick={() => handleLinkClick(TEST_RESULTS_URL_BASE)}>Test Results</MenuItem>
      <MenuItem onClick={() => handleLinkClick(ORDERS_URL)}>Orders</MenuItem>
      <MenuItem onClick={() => handleLinkClick(SETTINGS_URL)}>Profile</MenuItem>
      <MenuItem onClick={() => handleLinkClick(CMP_PRACTITIONER_URL)}>
        Practitioner application
      </MenuItem>
      <MenuItem onClick={() => handleLinkClick(CMP_STUDENT_APPLICATION_URL)}>
        Student application
      </MenuItem>
      <MenuItem onClick={logout} color="red" borderBottom="none">
        Log out
      </MenuItem>
    </>
  );

  return (
    <>
      <Menu
        opened={menuOpen}
        onChange={setMenuOpen}
        trigger="click"
        xOffset={-170}
        fullScreen={[true, true, false]}
      >
        <Menu.Target>
          <BurgerButton
            onClick={() => setMenuOpen(!menuOpen)}
            open={menuOpen}
            {...burgerButtonProps}
            bg={bg}
            borderBottomRightRadius={menuOpen ? 0 : 5}
          />
        </Menu.Target>
        <Menu.Dropdown
          bg={bg}
          borderRadius={10}
          borderTopRightRadius={0}
          minWidth={400}
          maxWidth={600}
          px={40}
          py={40}
          data-component-name="BurgerMenu"
          render={(children, { fullScreen, props }) =>
            fullScreen ? (
              <Box {...props} overflow="auto" borderRadius={0}>
                <Group alignItems="center" justifyContent="flex-end">
                  <CloseButton
                    color="white"
                    onClick={() => setMenuOpen(false)}
                    size={null}
                    mr="-4px"
                  />
                </Group>
                {mobileBurgerMenuContent}
              </Box>
            ) : (
              <Box {...props} overflow="auto" maxHeight="calc(100vh - 140px)">
                {burgerMenuContent}
              </Box>
            )
          }
        >
          {burgerMenuContent}
        </Menu.Dropdown>
      </Menu>
    </>
  );
}
